import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { getCoreRowModel, getPaginationRowModel } from '@tanstack/react-table';

import { getVisitFacilityJourney } from '../../../../services/visit.service';
import { makeVisitJourneyColumns } from '../../../../utils/columns/makeVisitJourneyColumns';
import { Table, useNewTable } from '../../../common/table/index';
import VisitJourneyStats from './VisitJourneyStats';
import { VisitStats } from './VisitJourneyStats';
import FormInputCollection from '../../../common/FormInputCollection/FormInputCollection';
import { FilterableTypes } from '../../../../types/FilterableTypes';
import { FormatForApi } from '../../../../utils/Format';

export interface JourneyFacility {
  journeyStep?: number;
  /** Unique per-row id — facilityId repeats when a visit revisits a facility (ping-pong),
   * so this synthetic id is used as the React/table key and click lookup key.
   * `id` mirrors `rowId` because the shared table keys rows on `row.id`. */
  id: string;
  rowId: string;
  facilityId: string;
  facilityName: string;
  firstSeenDateTime: string;
  lastSeenDateTime: string;
  totalSightings: number;
  averageTemperatureF: number;
  averageTemperatureC: number;
  stationIataCode: string;
  closestFacilityLogId: string;
  location?: { latitude: number; longitude: number };
  locationMethodDescr?: string;
  locationMethod?: number;
}

interface VisitJourneyPanelProps {
  visitId: string;
  activeIdx: number | null;
  canViewSightings?: boolean;
  showRaisedStats?: boolean;
  raisedStatsTop?: number | string;
  raisedStatsBottomMargin?: number;
  tableLeftMargin?: number | string;
  onViewSightings: (facilityId: string, firstSeenDateTime: string, lastSeenDateTime: string, closestFacilityLogId: string) => void;
  onFacilitiesChange: (facilities: JourneyFacility[]) => void;
  onFocusMarker: (index: number) => void;
  onLoadingChange: (loading: boolean) => void;
  onErrorChange: (error: string | null) => void;
}

const calcDuration = (from: string, to: string): string => {
  const ms = moment(to).diff(moment(from));
  if (ms <= 0) return '—';
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return '<1m';
};

// Normalise whatever shape the facility-journey endpoint returns into a segment array
const extractSegments = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.facilityJourney)) return res.facilityJourney;
  if (Array.isArray(res?.data?.facilityJourney)) return res.data.facilityJourney;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.results)) return res.results;
  return [];
};

export default function VisitJourneyPanel({
  visitId,
  activeIdx,
  canViewSightings = true,
  showRaisedStats = true,
  raisedStatsTop = '-40px',
  raisedStatsBottomMargin = 0,
  tableLeftMargin = '10px',
  onViewSightings,
  onFacilitiesChange,
  onFocusMarker,
  onLoadingChange,
  onErrorChange,
}: VisitJourneyPanelProps) {
  const [facilities, setFacilities] = useState<JourneyFacility[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastSeenFrom, setLastSeenFrom] = useState<Date | null>(null);
  const [lastSeenTo, setLastSeenTo] = useState<Date | null>(null);
  const [submittedLastSeenFrom, setSubmittedLastSeenFrom] = useState<Date | null>(null);
  const [submittedLastSeenTo, setSubmittedLastSeenTo] = useState<Date | null>(null);

  const facilityJourneyParams = useMemo(() => {
    if (!submittedLastSeenFrom || !submittedLastSeenTo) return undefined;

    return {
      filters: [
        {
          lastSeenDateTime: FormatForApi(moment(submittedLastSeenFrom), true),
          filterType: FilterableTypes.GreaterThanOrEqual,
          and: {
            lastSeenDateTime: FormatForApi(moment(submittedLastSeenTo), false),
            filterType: FilterableTypes.LessThanOrEqual,
          },
        },
      ],
    };
  }, [submittedLastSeenFrom, submittedLastSeenTo]);

  useEffect(() => {
    if (!visitId) return;

    setLoading(true);

    onLoadingChange(true);
    onErrorChange(null);

    getVisitFacilityJourney(visitId, facilityJourneyParams)
      .then((res) => {
        const segments = extractSegments(res);

        if (!segments.length) {
          const message = 'No data to display. Please try again later.';

          setFacilities([]);
          onFacilitiesChange([]);

          onErrorChange(message);

          return;
        }

        const enriched: JourneyFacility[] = segments.map((f: any, i: number) => {
          const rowId = `${i}-${f.closestFacilityId ?? 'na'}-${f.firstSeenDateTime ?? ''}`;
          const avgTempC: number = f.averageTemperature ?? 0;
          const avgTempF: number = (avgTempC * 9) / 5 + 32;
          const location = f.coordinates?.latitude != null ? f.coordinates : undefined;
          return {
            id: rowId,
            rowId,
            facilityId: f.closestFacilityId ?? '',
            facilityName: f.closestFacilityName ?? '',
            stationIataCode: f.stationIataCode ?? '',
            firstSeenDateTime: f.firstSeenDateTime ?? '',
            lastSeenDateTime: f.lastSeenDateTime ?? '',
            totalSightings: f.sightingsCount ?? 0,
            averageTemperatureC: avgTempC,
            averageTemperatureF: parseFloat(avgTempF.toFixed(1)),
            closestFacilityLogId: f.closestFacilityLogId ?? '',
            location,
            journeyStep: i + 1,
          };
        });

        setFacilities(enriched);
        onFacilitiesChange(enriched);

        onErrorChange(null);
      })
      .catch((err) => {
        console.error('Failed to load visit facility journey:', err);

        const message = 'No data to display. Please try again later.';

        setFacilities([]);
        onFacilitiesChange([]);

        onErrorChange(message);
      })
      .finally(() => {
        setLoading(false);

        onLoadingChange(false);
      });
  }, [visitId, facilityJourneyParams]);

  const stats: VisitStats = useMemo(() => {
    if (!facilities.length) return { count: 0, dwell: '—', avgTemp: '—', rssi: '—' };
    const totalSightings = facilities.reduce((a, f) => a + f.totalSightings, 0);
    const avgTemp = (facilities.reduce((a, f) => a + f.averageTemperatureF, 0) / facilities.length).toFixed(1);
    const sorted = [...facilities].sort((a, b) => new Date(a.firstSeenDateTime).getTime() - new Date(b.firstSeenDateTime).getTime());
    return {
      count: totalSightings,
      dwell: calcDuration(sorted[0].firstSeenDateTime, sorted[sorted.length - 1].lastSeenDateTime),
      avgTemp: `${avgTemp}°F`,
      rssi: '—',
    };
  }, [facilities]);

  const tableFacilities = useMemo(() => [...facilities].reverse(), [facilities]);

  const fetchAsync = useCallback(async () => {
    return {
      results: tableFacilities,
      totalCount: tableFacilities.length,
      count: tableFacilities.length,
      skipped: 0,
      status: 200,
    };
  }, [tableFacilities]);

  const columns = useMemo(
    () =>
      makeVisitJourneyColumns((rowId: string) => {
        const facility = facilities.find((f) => f.rowId === rowId);

        if (facility) {
          onViewSightings(
            facility.facilityId,
            facility.firstSeenDateTime ?? '',
            facility.lastSeenDateTime ?? '',
            facility.closestFacilityLogId ?? '',
          );
        }
      }, canViewSightings),
    [onViewSightings, facilities, canViewSightings],
  );

  const tableParams = useMemo(() => ({}), []);

  const handleRowClick = useCallback(
    (row: JourneyFacility) => {
      const idx = facilities.findIndex((f) => f.rowId === row.rowId);
      if (idx >= 0) onFocusMarker(idx);
    },
    [facilities, onFocusMarker],
  );

  const table = useNewTable<JourneyFacility>({
    columns,
    fetchAsync,
    params: tableParams,
    onRowClick: handleRowClick,
  });

  const tableActiveIdx = useMemo(() => {
    if (activeIdx === null || !facilities.length) return null;

    return facilities.length - 1 - activeIdx;
  }, [activeIdx, facilities.length]);

  if (loading) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1, py: 6 }}>
        <CircularProgress size={28} sx={{ color: '#7c3aed' }} />
        <Typography variant='caption' color='text.secondary'>
          Loading journey...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'visible' }}>
      {showRaisedStats && (
        <Box
          sx={{
            position: 'absolute',
            top: raisedStatsTop,
            left: 0,
            right: 0,
            zIndex: 40,
            pb: raisedStatsBottomMargin ? `${raisedStatsBottomMargin}px` : 0,
          }}
        >
          <VisitJourneyStats stats={stats} />
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          pt: showRaisedStats ? `calc(64px + ${raisedStatsBottomMargin}px)` : 2,
          '& .ui-table': {
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          },
          '& .ui-table__card': {
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          },
          '& .ui-table__scroll': {
            flex: '1 1 auto',
            minHeight: 0,
            maxHeight: 'none !important',
            overflowY: 'auto !important',
            overflowX: 'auto !important',
          },
          '& .ui-table__pagination': {
            flex: '0 0 auto',
          },
          ...(tableActiveIdx !== null && {
            [`& tbody tr:nth-of-type(${tableActiveIdx + 1})`]: {
              backgroundColor: '#eef2ff !important',
              outline: '2px solid #94a3b8',
              outlineOffset: '-2px',
            },
          }),
        }}
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Box sx={{ flex: '0 0 auto', px: 1.25, py: 1, borderTop: '1px solid #dbe5f1', bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 1.5 }}>
              <FormInputCollection label='Last Seen' style={{ margin: 0 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <DatePicker
                    label='From'
                    value={lastSeenFrom ? moment(lastSeenFrom) : null}
                    onChange={(value) => setLastSeenFrom(value?.isValid() ? value.toDate() : null)}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                  <DatePicker
                    label='To'
                    value={lastSeenTo ? moment(lastSeenTo) : null}
                    onChange={(value) => setLastSeenTo(value?.isValid() ? value.toDate() : null)}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                  <Tooltip title='Search Last Seen range'>
                    <IconButton
                      size='small'
                      aria-label='Search Last Seen range'
                      onClick={() => {
                        setSubmittedLastSeenFrom(lastSeenFrom);
                        setSubmittedLastSeenTo(lastSeenTo);
                      }}
                    >
                      <SearchIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title='Clear Last Seen filters'>
                    <IconButton
                      size='small'
                      aria-label='Clear Last Seen filters'
                      onClick={() => {
                        setLastSeenFrom(null);
                        setLastSeenTo(null);
                        setSubmittedLastSeenFrom(null);
                        setSubmittedLastSeenTo(null);
                      }}
                    >
                      <RefreshIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>
              </FormInputCollection>
            </Box>
          </Box>
        </LocalizationProvider>
        <Box
          sx={{
            ml: tableLeftMargin,
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            '& .ui-table__card': { borderRadius: 4, borderColor: '#dbe5f1', boxShadow: 'none' },
            '& .ui-table thead tr th': { backgroundColor: '#e7edf5', color: '#334155', fontWeight: 700, fontSize: 12 },
            '& .ui-table tbody tr td': { color: '#475569', fontSize: 13 },
            '& .MuiTableCell-root': { py: canViewSightings ? 0.625 : 1.2, px: 1.25 },
          }}
        >
          <Table
            {...table}
            data={tableFacilities}
            showSearchBar={false}
            showColumnToggler={false}
            showAction={false}
            getCoreRowModel={getCoreRowModel()}
            getPaginationRowModel={getPaginationRowModel()}
            totalRows={tableFacilities.length}
            serverSide={false}
            initialState={{ pagination: { pageSize: 100, pageIndex: 0 } }}
          />
          {!loading && facilities.length === 0 && (
            <Typography variant='body2' color='text.secondary' align='center' sx={{ py: 2 }}>
              No journey data found for this visit.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
